import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, ShoppingBag, DollarSign, Package, 
  Clock, Award, Star, Zap, Rocket, Activity, Target,
  BarChart3, PieChart, Calendar, ArrowUpRight, ArrowDownRight,
  TrendingDown, Bell, Settings, Plus, Download, Filter
} from 'lucide-react';
import axios from 'axios';

function DashboardOverview({ user, isMobile, setCurrentPage }) {
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    conversion: 42,
    growth: 28,
    retention: 86,
    satisfaction: 94
  });

  // Icon mapping object
  const iconComponents = {
    DollarSign: DollarSign,
    ShoppingBag: ShoppingBag,
    Users: Users,
    Package: Package,
    Clock: Clock,
    Award: Award,
    TrendingUp: TrendingUp,
    TrendingDown: TrendingDown,
    BarChart3: BarChart3,
    Activity: Activity,
    Target: Target,
    Zap: Zap,
    Rocket: Rocket,
    PieChart: PieChart,
    Calendar: Calendar,
    ArrowUpRight: ArrowUpRight,
    ArrowDownRight: ArrowDownRight,
    Bell: Bell,
    Settings: Settings,
    Plus: Plus,
    Download: Download,
    Filter: Filter,
    Star: Star
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      if (user?.token) {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
          apiType: "getDashboardStats"
        }, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'usertypeauth': 'User'
          }
        });

        if (response.data.success) {
          // Map the stats to ensure icon is a valid component
          const statsWithValidIcons = response.data.stats?.map(stat => {
            // If icon is a string, get the component from mapping
            if (typeof stat.icon === 'string') {
              return {
                ...stat,
                icon: iconComponents[stat.icon] || DollarSign
              };
            }
            return stat;
          }) || [];
          
          setStats(statsWithValidIcons);
          setRecentActivity(response.data.recentActivity || []);
        }
      } else {
        // Mock data for demo
        const mockStats = user?.role === 'staff' ? [
          { 
            label: 'Total Revenue', 
            value: '$24,857', 
            change: '+12.5%', 
            trend: 'up', 
            icon: 'DollarSign',
            color: 'from-emerald-500 to-teal-400' 
          },
          { 
            label: 'Today Orders', 
            value: '48', 
            change: '+8.2%', 
            trend: 'up', 
            icon: 'ShoppingBag', 
            color: 'from-blue-500 to-cyan-400' 
          },
          { 
            label: 'Active Customers', 
            value: '2,672', 
            change: '+23.1%', 
            trend: 'up', 
            icon: 'Users', 
            color: 'from-purple-500 to-pink-400' 
          },
          { 
            label: 'Products Sold', 
            value: '1,486', 
            change: '-2.3%', 
            trend: 'down', 
            icon: 'Package', 
            color: 'from-amber-500 to-orange-400' 
          }
        ] : [
          { 
            label: 'Total Orders', 
            value: '12', 
            change: '+2', 
            trend: 'up', 
            icon: 'ShoppingBag', 
            color: 'from-blue-500 to-cyan-400' 
          },
          { 
            label: 'Pending Orders', 
            value: '2', 
            change: '-1', 
            trend: 'down', 
            icon: 'Clock', 
            color: 'from-amber-500 to-orange-400' 
          },
          { 
            label: 'Completed Orders', 
            value: '10', 
            change: '+3', 
            trend: 'up', 
            icon: 'Award', 
            color: 'from-emerald-500 to-teal-400' 
          },
          { 
            label: 'Total Spent', 
            value: '$1,480', 
            change: '+$120', 
            trend: 'up', 
            icon: 'DollarSign', 
            color: 'from-purple-500 to-pink-400' 
          }
        ];

        const mockActivity = user?.role === 'staff' ? [
          { id: 1, type: 'order', message: 'New order #ORD-7842 received', time: '2 min ago', icon: '🛒', color: 'purple' },
          { id: 2, type: 'product', message: 'Product "Wagyu Ribeye" added to inventory', time: '15 min ago', icon: '🥩', color: 'green' },
          { id: 3, type: 'customer', message: 'New premium customer registered', time: '1 hour ago', icon: '👤', color: 'blue' },
          { id: 4, type: 'system', message: 'System maintenance completed', time: '2 hours ago', icon: '⚙️', color: 'gray' }
        ] : [
          { id: 1, type: 'order', message: 'Order #ORD-1234 delivered successfully', time: '2 days ago', icon: '📦', color: 'green' },
          { id: 2, type: 'order', message: 'New order #ORD-1235 placed', time: '3 days ago', icon: '🛒', color: 'purple' },
          { id: 3, type: 'rating', message: 'You left a 5-star review', time: '1 week ago', icon: '⭐', color: 'amber' },
          { id: 4, type: 'account', message: 'Profile information updated', time: '2 weeks ago', icon: '👤', color: 'blue' }
        ];

        // Convert icon strings to components for mock data
        const mockStatsWithIcons = mockStats.map(stat => ({
          ...stat,
          icon: iconComponents[stat.icon] || DollarSign
        }));

        setStats(mockStatsWithIcons);
        setRecentActivity(mockActivity);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Fallback to mock data on error
      const fallbackStats = [
        { 
          label: 'Total Orders', 
          value: '0', 
          change: '+0%', 
          trend: 'up', 
          icon: iconComponents.ShoppingBag, 
          color: 'from-blue-500 to-cyan-400' 
        },
        { 
          label: 'Revenue', 
          value: '$0', 
          change: '+0%', 
          trend: 'up', 
          icon: iconComponents.DollarSign, 
          color: 'from-emerald-500 to-teal-400' 
        }
      ];
      
      setStats(fallbackStats);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'addProduct':
        setCurrentPage('dashboard');
        break;
      case 'salesReport':
        setCurrentPage('dashboard');
        break;
      case 'viewUsers':
        setCurrentPage('dashboard');
        break;
      case 'continueShopping':
        setCurrentPage('shop');
        break;
      case 'contactSupport':
        setCurrentPage('home');
        setTimeout(() => {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        break;
      case 'viewOrders':
        setCurrentPage('dashboard');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-16 h-16 border-4 border-pink-500 border-b-transparent rounded-full animate-spin animation-delay-1000"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Welcome back, <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">{user?.name || user?.email}</span>!
          </h2>
          <p className="text-gray-400">
            {user?.role === 'staff' 
              ? 'Here\'s what\'s happening with your butchery today.' 
              : 'Track your orders and manage your account.'}
            <span className="ml-2 px-2 py-1 bg-gradient-to-r from-gray-800 to-gray-900 text-gray-300 text-xs rounded-full">
              Last updated: Today
            </span>
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Today
          </button>
          <button className="p-2 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors">
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(performanceMetrics).map(([key, value], index) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-400 capitalize">{key}</div>
              <Activity className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{value}%</div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${value}%` }}
                transition={{ duration: 1, delay: index * 0.2 }}
                className={`h-full ${
                  key === 'conversion' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                  key === 'growth' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                  key === 'retention' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                  'bg-gradient-to-r from-amber-500 to-orange-500'
                }`}
              />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Stats Grid */}
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-4'} gap-6`}>
        {stats.map((stat, index) => {
          const IconComponent = stat.icon || DollarSign;
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="relative group"
            >
              {/* Glow Effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${stat.color} rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
              
              {/* Card */}
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                    stat.trend === 'up' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {stat.change}
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-2">{stat.value}</h3>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                
                {/* Progress line */}
                <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '75%' }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                    className={`h-full bg-gradient-to-r ${stat.color}`}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2"
        >
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6 h-full">
            {/* Activity Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Recent Activity
              </h2>
              <button className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1">
                View All
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>

            {/* Activity List */}
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-800/30 hover:bg-gray-800/50 transition-colors group cursor-pointer"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                    activity.color === 'green' ? 'bg-emerald-500/20' :
                    activity.color === 'blue' ? 'bg-blue-500/20' :
                    activity.color === 'purple' ? 'bg-purple-500/20' :
                    activity.color === 'amber' ? 'bg-amber-500/20' :
                    'bg-gray-500/20'
                  }`}>
                    {activity.icon}
                  </div>
                  
                  <div className="flex-1">
                    <p className="text-white font-medium">{activity.message}</p>
                    <p className="text-sm text-gray-400">{activity.time}</p>
                  </div>
                  
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-1"
        >
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6 h-full">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Quick Stats
            </h2>

            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400">Performance</span>
                  <span className="text-sm text-emerald-400">Optimal</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '85%' }}
                    transition={{ duration: 1 }}
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400">Uptime</span>
                  <span className="text-sm text-cyan-400">45 days</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '99.8%' }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400">Customer Satisfaction</span>
                  <Star className="w-4 h-4 text-amber-500" />
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '94%' }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Quick Stats Numbers */}
            <div className="mt-8 pt-6 border-t border-gray-800">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 rounded-xl bg-gray-800/30">
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-sm text-gray-400">Support</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-gray-800/30">
                  <div className="text-2xl font-bold text-white">99.8%</div>
                  <div className="text-sm text-gray-400">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          Quick Actions
        </h3>
        <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-4'} gap-4`}>
          {user?.role === 'staff' ? (
            <>
              <QuickActionButton
                icon={<Plus className="w-5 h-5" />}
                label="Add Product"
                onClick={() => handleQuickAction('addProduct')}
                color="from-emerald-500 to-teal-500"
              />
              <QuickActionButton
                icon={<BarChart3 className="w-5 h-5" />}
                label="View Analytics"
                onClick={() => handleQuickAction('salesReport')}
                color="from-blue-500 to-cyan-500"
              />
              <QuickActionButton
                icon={<Users className="w-5 h-5" />}
                label="Manage Users"
                onClick={() => handleQuickAction('viewUsers')}
                color="from-purple-500 to-pink-500"
              />
              <QuickActionButton
                icon={<Download className="w-5 h-5" />}
                label="Export Report"
                onClick={() => {}}
                color="from-amber-500 to-orange-500"
              />
            </>
          ) : (
            <>
              <QuickActionButton
                icon={<ShoppingBag className="w-5 h-5" />}
                label="Continue Shopping"
                onClick={() => handleQuickAction('continueShopping')}
                color="from-emerald-500 to-teal-500"
              />
              <QuickActionButton
                icon={<Package className="w-5 h-5" />}
                label="View Orders"
                onClick={() => handleQuickAction('viewOrders')}
                color="from-blue-500 to-cyan-500"
              />
              <QuickActionButton
                icon={<Bell className="w-5 h-5" />}
                label="Support Center"
                onClick={() => handleQuickAction('contactSupport')}
                color="from-purple-500 to-pink-500"
              />
              <QuickActionButton
                icon={<Settings className="w-5 h-5" />}
                label="Account Settings"
                onClick={() => setCurrentPage('profile')}
                color="from-amber-500 to-orange-500"
              />
            </>
          )}
        </div>
      </div>

      {/* Performance Chart Placeholder */}
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Performance Overview
          </h3>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-800 text-gray-300 rounded-lg text-sm">Week</button>
            <button className="px-3 py-1 bg-gray-800 text-gray-300 rounded-lg text-sm">Month</button>
            <button className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm">Year</button>
          </div>
        </div>
        
        <div className="h-64 flex items-end gap-2 md:gap-4">
          {[65, 80, 45, 90, 75, 85, 95].map((height, index) => (
            <motion.div
              key={index}
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ duration: 1, delay: index * 0.1 }}
              className="flex-1 flex flex-col items-center"
            >
              <div className="w-full bg-gradient-to-t from-purple-500 to-pink-600 rounded-t-lg"></div>
              <div className="text-xs text-gray-500 mt-2">Day {index + 1}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickActionButton({ icon, label, onClick, color = 'from-purple-500 to-pink-500' }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`bg-gradient-to-br from-gray-900 to-black border border-gray-800 p-6 rounded-2xl hover:border-gray-700 transition-all group`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          {icon}
        </div>
        <div className="text-left">
          <div className="font-bold text-white text-lg">{label}</div>
          <div className="text-sm text-gray-400 mt-1">Click to access</div>
        </div>
      </div>
    </motion.button>
  );
}

export default DashboardOverview;